import { useState } from "react";
import { Alert, Button, Paper, Stack, TextField, Typography } from "@mui/material";

import { failureText } from "../lib/reports.js";

/**
 * A question in words, turned into a report to check.
 *
 * What is said here is what the kernel does (`POST /insights/ask`). The model is shown
 * the names of the types and fields this person can open and the question, never a record
 * or a figure; what it proposes is checked by the same parser as a report built by hand
 * and opens in the form below, run and then to be read, changed and saved. It is a
 * proposal and the figures still come from the kernel under this person's own access.
 *
 * Refusals are said as what they are. The model declining ("that needs a forecast") is a
 * sentence about the question; the day's limit is a sentence about the day; a failure to
 * reach the model is a sentence about the platform. None of them is drawn as "no results".
 */
export default function AskBox({ onAsk }) {
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState(null);

  const go = async () => {
    const q = question.trim();
    if (!q || busy) return;
    setBusy(true);
    setOutcome(null);
    try {
      setOutcome(await onAsk(q));
    } catch (e) {
      setOutcome({ kind: "failed", text: failureText(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack sx={{ gap: 1.5 }} component="section" aria-label="Ask in words">
        <Typography variant="subtitle2" component="h3">Ask in words</Typography>
        <Typography variant="caption" color="text.secondary">
          Say what you want to count or add up and how to break it down, and it opens below as a report to check.
          Only the names of what you can open, and your question, go to the model: no records and no figures.
        </Typography>
        <Stack direction="row" sx={{ gap: 1, alignItems: "flex-start", flexWrap: "wrap" }}>
          <TextField
            size="small" label="Your question" value={question} multiline maxRows={4}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); go(); } }}
            slotProps={{ htmlInput: { maxLength: 500 } }}
            placeholder="How many invoices were issued each month this year?"
            sx={{ flex: "1 1 320px", minWidth: 0 }}
          />
          <Button variant="outlined" onClick={go} disabled={busy || !question.trim()}>
            {busy ? "Asking…" : "Ask"}
          </Button>
        </Stack>
        {outcome?.kind === "spec" ? (
          <Alert severity="success" role="status">
            {outcome.says} It is open below and has been run. Check it is what you meant before you save it.
            {outcome.leftOut?.length ? ` ${outcome.leftOut.join(" and ")} could not be carried into the form, so they were left out.` : ""}
            {` ${outcome.remaining} of ${outcome.perDay} questions left today.`}
          </Alert>
        ) : outcome?.kind === "refused" ? (
          <Alert severity="info" role="status">
            {outcome.reason} {`${outcome.remaining} of ${outcome.perDay} questions left today.`}
          </Alert>
        ) : outcome?.kind === "failed" ? (
          <Alert severity="warning" role="alert">{outcome.text}</Alert>
        ) : null}
      </Stack>
    </Paper>
  );
}
